import { useMutation, useQuery } from '@apollo/client';
// @ts-ignore
import nestedProperty from 'nested-property';
import {
  allCitiesQuery,
  allFoodCategoriesQuery,
  productCategories as productCategoriesQuery,
} from '../../graphql/queries';
import React, {
  useCallback,
  useContext,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  updateStore,
  updateStoreSuperAdminMutation,
} from '../../graphql/mutations';
import { localizeStore } from './StoreMapHelper';
import { PickpackLonLat } from './MerchantModel';
import { DateTimeHelpers } from '../../tools/DateTimeHelpers';
import { isValidUrl } from '@/utils/url';
import { StoreImageCarouselView } from './StoreImageView';
import { Badge, Input, InputGroup, InputGroupText } from 'reactstrap';
import HelpPopover from '../../components/HelpPopover';
import { getObjectDiff } from '@/utils/objDiff';
import { Communicator } from '../../tools/Communication';
import {
  EditItem,
  EditSection,
  LoadingContainer,
} from '../../components/Layout';
import { IconButton } from '../../components/Button/Button';
import {
  FaMapMarkedAlt,
  FaAddressCard,
  FaExternalLinkAlt,
  FaArrowUp,
  FaArrowDown,
} from 'react-icons/all';
import i18n from '@/utils/i18n';
import { isValidEmailInput } from '@/utils/email';
import { MultiSelect } from 'primereact/multiselect';
import {
  CanAccessPartnerData,
  ViewerContext,
} from '../../providers/ViewerContextProvider';
import { FaEdit, FaLock, FaSearch, FaTimes } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { Routes } from '@/utils/routes';
import { ZohoStoreButton } from '../../components/Button/ZohoStoreButton';
import { SalesforceContacts } from './SalesforceContacts';
import { isNullOrWhitespace } from '@/utils/stringHelper';
import { Button } from 'reactstrap';
import { useStoreQuery } from '../../graphql/queries/storeAdminInfo.generated';
import { StoreAdminFragmentFragment } from '../../graphql/fragments/storeAdminInfo.generated';
import { ContentSection } from '../../components/Layout/ContentSection';
import ContainerTypeDisplay from '../../components/ContainerType/ContainerTypeDisplay';
import L, { LatLngExpression } from 'leaflet';
import {
  VytalCheckboxLabelContainer,
  CheckboxLabel,
} from '@/pages/AdminStoreDetail/Menu/ProductItem/ProductOrderSheet/ExtraSelector/ExtraGroupEditModal/styled';
import Checkbox from '@/components/Checkbox';
import { isValidPostalCode, normalizePostalCode } from '@/utils/postal';
import { getPreferredLocaleOptions } from '@/utils/localeOptions';

type MerchantGeneralTabProps = {
  externalPickpackId: string | null;
  title: string;
};

declare global {
  interface Window {
    L: any;
  }
}

type StoreMapRef = {
  storePosMarker: any | null;
  storeMap: any | null;
  isSetPosByStoreClickMode: boolean;
};

type ContainerGroup = NonNullable<
  StoreAdminFragmentFragment['container_groups']
>[number];

export const MerchantGeneralTab: React.FC<MerchantGeneralTabProps> = ({
  externalPickpackId,
  title,
}) => {
  const viewer = useContext(ViewerContext);
  const allowedToAccess = CanAccessPartnerData(viewer);
  const [storeUpdate, setStoreUpdate] =
    useState<StoreAdminFragmentFragment | null>(null);
  const [oldStoreUpdate, setOldStoreUpdate] =
    useState<StoreAdminFragmentFragment | null>(null);
  const [updateStoreMutation, { loading: updating }] = useMutation(
    allowedToAccess ? updateStoreSuperAdminMutation : updateStore,
  );
  const { data: locationData = { locations: [] }, loading: loadingCities } =
    useQuery(allCitiesQuery, { fetchPolicy: 'network-only' });
  const [lonValid, setLonValid] = useState(true);
  const [latValid, setLatValid] = useState(true);
  const [isSetPosByStoreClickMode, setIsPosByStoreClickMode] = useState(false);
  const mapRef = useRef(null);
  const storeMapRefs = useRef<StoreMapRef>({
    storePosMarker: null,
    storeMap: null,
    isSetPosByStoreClickMode: false,
  });
  storeMapRefs.current.isSetPosByStoreClickMode = isSetPosByStoreClickMode;

  function resetStore(storeIn: any) {
    setStoreUpdate(storeIn);
    setOldStoreUpdate(JSON.parse(JSON.stringify(storeIn)));
  }

  const { data = { store: null }, loading: loadingStore } = useStoreQuery({
    fetchPolicy: 'no-cache',
    skip: !externalPickpackId,
    variables: { id: externalPickpackId || '' },
    onCompleted: (storeData) => {
      // @ts-ignore
      if (storeData) {
        resetStore(storeData.store);
      }
    },
  });

  const { data: foodCategoryData = { __type: { enumValues: [] } } } = useQuery(
    allFoodCategoriesQuery,
  );
  const foodCategories: any = foodCategoryData.__type.enumValues.map(
    (foodCategory: any) => {
      return {
        label: i18n.t(`GeneralAdminInfo:Category:${foodCategory.name}`),
        value: foodCategory.name,
      };
    },
  );

  const lonlat = (storeUpdate && storeUpdate.lonlat) || {};
  useEffect(() => {
    if (L && mapRef.current) {
      const position: LatLngExpression = [
        lonlat.latitude || 0,
        lonlat.longitude || 0,
      ];
      try {
        storeMapRefs.current.storeMap = L.map(mapRef.current).setView(
          position,
          13,
        );
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        }).addTo(storeMapRefs.current.storeMap);
        storeMapRefs.current.storeMap.on('click', function (e: any) {
          if (!storeMapRefs.current.isSetPosByStoreClickMode) {
            return;
          }
          const popLocation = e.latlng;
          setStoreUpdate((oldStoreUpdate: any) => ({
            ...oldStoreUpdate,
            lonlat: {
              latitude: popLocation.lat,
              longitude: popLocation.lng,
            },
          }));
          setIsPosByStoreClickMode(false);
        });
        storeMapRefs.current.storePosMarker = null;
      } catch (e) {}

      if (storeMapRefs && storeMapRefs.current) {
        if (!storeMapRefs.current.storePosMarker) {
          storeMapRefs.current.storePosMarker = L.marker(position);
          storeMapRefs.current.storePosMarker.addTo(
            storeMapRefs.current.storeMap,
          );
        } else {
          storeMapRefs.current.storePosMarker.setLatLng(position).update();
        }
      }
    }
  }, [lonlat.longitude, lonlat.latitude]);

  if (loadingStore || !storeUpdate || loadingCities) {
    return <LoadingContainer />;
  }

  const store: any = data.store;

  // @ts-ignore
  function handleChange({ target }) {
    const { name, value } = target;
    let formattedValue = value;

    if (target.hasAttribute) {
      const isNumber = target.hasAttribute('data-isnumber');
      const isDate = target.hasAttribute('data-isdate');
      if (isNumber) formattedValue = Number(value);
      if (isDate) {
        const date = (target as EventTarget & HTMLInputElement).valueAsDate;
        if (date != null) {
          formattedValue = DateTimeHelpers.toDateTimeOffsetString(date);
        } else {
          formattedValue = DateTimeHelpers.toDateTimeOffsetString(new Date(0));
        }
      }
    }
    setStoreUpdate((oldStoreUpdate: any) => {
      const newStoreUpdate = { ...JSON.parse(JSON.stringify(oldStoreUpdate)) };
      nestedProperty.set(newStoreUpdate, name, formattedValue);
      return newStoreUpdate;
    });
  }

  function handleAcceptedContainerGroupsChange({ target }: { target: any }) {
    setStoreUpdate((oldStoreUpdate: any) => {
      let { value: containerGroupIds } = target;
      if (!allowedToAccess) {
        const deleted = oldStoreUpdate.accepted_container_type_group_ids.filter(
          (id: number) => !containerGroupIds.includes(id),
        );
        containerGroupIds = containerGroupIds.concat(
          deleted
            .map((id: number) =>
              oldStoreUpdate.container_groups.find(
                (cg: ContainerGroup) => cg.id_int === id,
              ),
            )
            .filter((cg: ContainerGroup) => cg != null && !cg.can_edit)
            .map((cg: ContainerGroup) => cg.id_int),
        );
      }
      return {
        ...oldStoreUpdate,
        accepted_container_type_group_ids: containerGroupIds,
      };
    });
  }

  function handleCityChange(e: any) {
    const cityId = e.target.value.split(' - ')[1].replace(')', '');
    console.log('cityId', cityId);
    const city = locationData.locations.find(
      (location: any) => location.id === cityId,
    );
    if (city) {
      setStoreUpdate((oldStoreUpdate: any) => ({
        ...oldStoreUpdate,
        city_id: cityId,
        linked_city: city,
      }));
    }
  }

  function handleSave() {
    const getMutationInput = (storeIn: any) => {
      if (storeIn.address) {
        const address = { ...storeIn.address };
        storeIn.address = {
          streetname: address.streetname && address.streetname.trim(),
          streetno: address.streetno && address.streetno.trim(),
          street_extra: address.street_extra && address.street_extra.trim(),
          zip: address.zip && address.zip.trim(),
          city: address.city && address.city.trim(),
          state: address.state && address.state.trim(),
        };
      }
      const lonlat = {
        longitude: Number(storeIn.lonlat.longitude),
        latitude: Number(storeIn.lonlat.latitude),
      };
      const mutationInput: any = {
        name: storeIn.name && storeIn.name.trim(),
        address: {
          ...storeIn.address,
          __typename: undefined,
        },
        lonlat,
        opening_hours_multi: storeIn.opening_hours_multi_raw.map(
          (d: object) => ({
            ...d,
            __typename: undefined,
          }),
        ),
        special_opening_hours_multi: (
          storeIn.special_opening_hours_multi || []
        ).map((d: object) => ({
          ...d,
          __typename: undefined,
        })),
        delivery_hours: (storeIn.delivery_hours_raw || []).map((d: object) => ({
          ...d,
          __typename: undefined,
        })),
        preorder_hours: (storeIn.preorder_hours_raw || []).map((d: object) => ({
          ...d,
          __typename: undefined,
        })),
        min_delivery_order_value: Number(storeIn.min_delivery_order_value),
        min_delivery_time: Number(storeIn.min_delivery_time),
        delivery_fee: Number(storeIn.delivery_fee),
        food_info_de: storeIn.description_de, // the fields food_info_(de|en) can be removed once the legacy App is not longer used
        food_info_en: storeIn.description_en,
        banner: storeIn.banner,
        phone_number: storeIn.phone_number,
        website: storeIn.website,
        min_order_time: Number(storeIn.min_order_time),
        cancelation_time: Number(storeIn.cancelation_time),
        preorder_sms_number: storeIn.preorder_sms_number,
        partner_newsletter_email: storeIn.partner_newsletter_email,
        wolt_url: storeIn.wolt_url,
        lieferando_url: storeIn.lieferando_url,
        habgusta_url: storeIn.habgusta_url,
        mjam_url: storeIn.mjam_url,
        too_good_to_go_url: storeIn.too_good_to_go_url,
        rappi_url: storeIn.rappi_url,
        didi_food_url: storeIn.didi_food_url,
        labonapp_url: storeIn.labonapp_url,
        ubereats_url: storeIn.ubereats_url,
        split_app_url: storeIn.split_app_url,
        foodora_url: storeIn.foodora_url,
        food_categories: storeIn.food_categories,
        menu_link: storeIn.menu_link,
        accepted_container_type_group_ids:
          storeIn.accepted_container_type_group_ids,
        city_id: storeIn.city_id,
        preferred_locale: storeIn.preferred_locale || null,
      };

      for (const key of Object.keys(storeIn).filter((key) =>
        key.startsWith('description_'),
      )) {
        mutationInput[key] = storeIn[key];
      }

      if (allowedToAccess) {
        // add admin only fields
        // @ts-ignore
        mutationInput.internal_comment = storeIn.internal_comment;
        mutationInput.external_id = (storeIn.external_id || '').trim() || null;
      }

      return mutationInput;
    };

    const mutationInput: any = getObjectDiff(
      getMutationInput(oldStoreUpdate),
      getMutationInput(storeUpdate),
    );
    console.log('mutationInput', mutationInput);
    updateStoreMutation({
      variables: {
        id: store.id,
        store: mutationInput,
      },
      refetchQueries: [
        {
          query: productCategoriesQuery,
          variables: {
            id: store.id,
            filter: 'ALL',
          },
        },
      ],
      update: (cache, { data }) => {
        const updatedStore = data.updateStore || data.updateStoreSuperAdmin;
        resetStore(updatedStore);
      },
    }).catch((err: any) => {
      alert('Error updating store: ' + err);
    });
  }

  function isItNumber(str: string) {
    return /^-?[0-9]+(e[0-9]+)?(\.[0-9]+)?$/.test(str);
  }

  function isLatitude(latStr: string) {
    const lat = Number(latStr);
    const isValid = isFinite(lat) && Math.abs(lat) <= 90;
    return isValid && isItNumber(latStr);
  }

  async function onChangeGooglePlaceIdClick() {
    const newGooglePlacesId = prompt(
      'Please paste the new Google Place Id:',
      '',
    );
    if (newGooglePlacesId && newGooglePlacesId.trim() !== '') {
      if (newGooglePlacesId.includes('http')) {
        return alert(
          'Error: Please paste the Google Place Id, not the URL. Check the opening hour docs in notion for more info.',
        );
      }

      await updateStoreMutation({
        variables: {
          id: store.id,
          store: {
            google_places_id: newGooglePlacesId,
          },
        },
        update: (cache, { data }) => {
          const updatedStore = data.updateStore || data.updateStoreSuperAdmin;
          resetStore(updatedStore);
        },
      }).catch((err: any) => {
        alert('Error setting Google Places Id: ' + err);
      });
    }
  }

  function isLongitude(lngStr: string) {
    const lng = Number(lngStr);
    const isValid = isFinite(lng) && Math.abs(lng) <= 180;
    return isValid && isItNumber(lngStr);
  }

  const localizeStoreFromAddress = () => {
    if (
      window.confirm(
        'Do you really want to overwrite the current longitude and latitude?',
      )
    ) {
      localizeStore(storeUpdate.address || {}, (lonlat: PickpackLonLat) => {
        setStoreUpdate((oldStoreUpdate: any) => ({
          ...oldStoreUpdate,
          lonlat,
        }));
        const position = [lonlat.latitude, lonlat.longitude];
        try {
          storeMapRefs.current.storeMap.setView(position, 13);
        } catch (e) {}
      });
    }
  };

  function swapFoodCategories(from: number, to: number) {
    if (storeUpdate == null) return;
    const array = storeUpdate.food_categories || [];
    if (to >= 0 && to < array.length) {
      const tmp = array[from];
      array[from] = array[to];
      array[to] = tmp;
      handleChange({ target: { name: 'food_categories', value: array } });
    }
  }

  if (!externalPickpackId) return <p>externalPickpackId is not available</p>;

  if (!store) return <p>Store {externalPickpackId} could not be loaded</p>;

  // @ts-ignore
  const isProd = window.__pp_stage === 'PROD';
  const appVersion = isProd ? 'app' : 'test-app';
  const storeDeepLink = `https://vytal.${appVersion}.link/?linktype=STORE&storeId=${encodeURIComponent(
    storeUpdate.id,
  )}`;

  // @ts-ignore
  return (
    <>
      <ContentSection title={title} collapsable={false} key="content">
        <EditSection
          title={i18n.t('GeneralAdminInfo:generalInformation')}
          onSave={handleSave}
          isLoading={updating}
        >
          {storeUpdate.permanently_removed && (
            <div className="alert alert-danger" role="alert">
              {i18n.t('GeneralAdminInfo:disabledStore')}
            </div>
          )}

          <EditItem title={i18n.t('Common:name')}>
            <div className="input-group mb-3">
              <span className="input-group-text" id="inputGroup-sizing-lg">
                {i18n.t('Common:name')}
              </span>
              <input
                type="text"
                className="form-control"
                name="name"
                onChange={handleChange}
                value={storeUpdate.name || ''}
              />
            </div>
            {allowedToAccess && (
              <div className="input-group mb-3">
                <span className="input-group-text" id="external-id-label">
                  {i18n.t('Invoices:externalId')}
                </span>
                <input
                  type="text"
                  className="form-control"
                  name="external_id"
                  onChange={handleChange}
                  value={storeUpdate.external_id || ''}
                  placeholder={i18n.t('Invoices:externalIdHelp')}
                />
              </div>
            )}
            {allowedToAccess && storeUpdate.brand?.id && (
              <div className="mb-3">
                <Link
                  to={Routes.legacyBrandEdit(storeUpdate.brand.id)}
                  className="tw:cursor-pointer btn btn-outline-dark"
                >
                  <FaEdit className="me-1" />
                  {i18n.t('PartnerSearch:brandLegacy')}:{' '}
                  {storeUpdate.brand.name ?? storeUpdate.brand.id}
                </Link>
              </div>
            )}
            {allowedToAccess && storeUpdate.external_zoho_crm_id && (
              <div className="mb-3">
                <ZohoStoreButton
                  externalZohoId={storeUpdate.external_zoho_crm_id}
                  label="View Store Account in Zoho"
                />
              </div>
            )}
          </EditItem>

          {/* Preferred Locale */}
          <EditItem title={i18n.t('GeneralAdminInfo:preferredLocale')}>
            <div className="row">
              <div className="col-sm-6">
                <div className="input-group mb-3">
                  <span
                    className="input-group-text"
                    id="preferred-locale-label"
                  >
                    {i18n.t('GeneralAdminInfo:preferredLocale')}
                  </span>
                  <select
                    className="form-control"
                    name="preferred_locale"
                    onChange={handleChange}
                    value={storeUpdate.preferred_locale || ''}
                  >
                    {getPreferredLocaleOptions().map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </EditItem>

          {/* Address */}
          <EditItem title={i18n.t('Common:address')}>
            <div className="row">
              <div className="col-sm-6">
                {/* Street */}
                <div className="input-group mb-3">
                  <span className="input-group-text" id="basic-addon1">
                    {i18n.t('Common:street')}
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    name="address.streetname"
                    onChange={handleChange}
                    value={
                      (storeUpdate.address && storeUpdate.address.streetname) ||
                      ''
                    }
                  />
                </div>

                {/* Street number */}
                <div className="input-group mb-3">
                  <span className="input-group-text" id="basic-addon1">
                    {i18n.t('Common:streetNumber')}
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    onChange={handleChange}
                    name="address.streetno"
                    value={
                      (storeUpdate.address && storeUpdate.address.streetno) ||
                      ''
                    }
                  />
                </div>

                {/* Street extra */}
                <div className="input-group mb-3">
                  <span className="input-group-text" id="basic-addon1">
                    {i18n.t('Common:streetExtra')}
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    onChange={handleChange}
                    name="address.street_extra"
                    value={
                      (storeUpdate.address &&
                        storeUpdate.address.street_extra) ||
                      ''
                    }
                  />
                </div>

                {/* Zip code */}
                <div className="input-group mb-3">
                  <span className="input-group-text" id="basic-addon1">
                    {i18n.t('Common:zipCode')}
                  </span>
                  <input
                    type="text"
                    maxLength={10}
                    className={
                      'form-control ' +
                      (!isValidPostalCode(
                        ((storeUpdate.address || {}) as any).zip,
                        storeUpdate.country_code,
                      )
                        ? 'is-invalid'
                        : '')
                    }
                    name="address.zip"
                    onChange={(e) => {
                      const normalized = normalizePostalCode(
                        e.target.value.substring(0, 10),
                        storeUpdate.country_code,
                      );
                      handleChange({
                        target: { name: 'address.zip', value: normalized },
                      });
                    }}
                    value={(storeUpdate.address || {}).zip || ''}
                  />
                </div>

                {/* City */}
                <div className="input-group mb-3">
                  <span className="input-group-text" id="basic-addon1">
                    {i18n.t('Common:city')}
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    name="address.city"
                    onChange={handleChange}
                    value={
                      (storeUpdate.address && storeUpdate.address.city) || ''
                    }
                    placeholder={i18n.t('GeneralAdminInfo:cityNameExample')}
                  />
                </div>

                {/* State */}
                <div className="input-group mb-3">
                  <span className="input-group-text" id="basic-addon1">
                    {i18n.t('Common:state')}
                  </span>
                  <input
                    type="text"
                    className="form-control"
                    name="address.state"
                    onChange={handleChange}
                    value={
                      (storeUpdate.address && storeUpdate.address.state) || ''
                    }
                    placeholder={i18n.t('GeneralAdminInfo:stateExample')}
                  />
                </div>

                {/* Country */}
                <div className="input-group mb-3">
                  <span className="input-group-text" id="basic-addon1">
                    {i18n.t('Common:country')}
                  </span>
                  <input
                    type="text"
                    disabled
                    className="form-control"
                    value={
                      storeUpdate.country_code
                        ? (i18n.t(
                            `Common:Country:${storeUpdate.country_code}`,
                          ) as string)
                        : ' - '
                    }
                  />
                </div>
              </div>

              <div className="col-sm-6">
                {/* Linked city */}
                <div className="input-group mb-3">
                  <span className="input-group-text" id="basic-addon1">
                    {i18n.t('GeneralAdminInfo:linkedCity')}
                  </span>
                  <select
                    className="form-control"
                    disabled={!allowedToAccess}
                    onChange={handleCityChange}
                    value={
                      storeUpdate.linked_city
                        ? `${storeUpdate.linked_city.name} (stores: ${storeUpdate.linked_city.store_count} - ${storeUpdate.linked_city.id})`
                        : ' - '
                    }
                  >
                    {[...locationData.locations]
                      .sort((a: any, b: any) => {
                        // Sort alphabetically
                        if (a.name < b.name) return -1;
                        if (a.name > b.name) return 1;
                        // If names are equal, sort by store count
                        return b.store_count - a.store_count;
                      })
                      .filter((location: any, index: number, self: any) => {
                        // Remove locations with the same name that have a lower store count
                        const firstIndex = self.findIndex(
                          (l: any) => l.name === location.name,
                        );
                        return firstIndex === index;
                      })
                      .map((location: any) => (
                        <option key={location.id}>
                          {location.name} (stores: {location.store_count} -{' '}
                          {location.id})
                        </option>
                      ))}
                  </select>
                </div>

                {/* Google place */}
                {allowedToAccess && (
                  <div className="input-group mb-3">
                    <span className="input-group-text" id="basic-addon1">
                      <FaLock style={{ marginRight: '5px' }} />{' '}
                      {i18n.t('GeneralAdminInfo:linkedGoogleMapsPlace')}
                    </span>
                    {storeUpdate.google_places_id ? (
                      <a
                        className="form-control"
                        target="_blank"
                        style={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        href={
                          'https://www.google.com/maps/place/?q=place_id:' +
                          encodeURIComponent(storeUpdate.google_places_id)
                        }
                        rel="noreferrer"
                      >
                        Place Id: {storeUpdate.google_places_id}
                      </a>
                    ) : (
                      <div className="form-control">not linked!</div>
                    )}
                    <Button
                      disabled={updating}
                      onClick={onChangeGooglePlaceIdClick}
                    >
                      Change
                    </Button>
                  </div>
                )}

                {/* Latitude */}
                <div className="input-group mb-3">
                  <span className="input-group-text" id="basic-addon1">
                    {i18n.t('GeneralAdminInfo:latitude')}
                  </span>
                  <input
                    type="number"
                    className={
                      'form-control ' + (!latValid ? 'is-invalid' : '')
                    }
                    name="lonlat.latitude"
                    autoComplete="off"
                    lang="en-US"
                    onChange={(e) => {
                      handleChange(e);
                      setLatValid(isLatitude(e.target.value));
                    }}
                    step="0.0000000000000001"
                    value={
                      (storeUpdate.lonlat && storeUpdate.lonlat.latitude) || ''
                    }
                    placeholder={i18n.t('GeneralAdminInfo:latitude')}
                  />
                </div>

                {/* Longitude */}
                <div className="input-group mb-3">
                  <span className="input-group-text" id="basic-addon1">
                    {i18n.t('GeneralAdminInfo:longitude')}
                  </span>
                  <input
                    type="number"
                    className={
                      'form-control ' + (!lonValid ? 'is-invalid' : '')
                    }
                    name="lonlat.longitude"
                    autoComplete="off"
                    lang="en-US"
                    onChange={(e) => {
                      handleChange(e);
                      setLonValid(isLongitude(e.target.value));
                    }}
                    step="0.0000000000000001"
                    value={
                      (storeUpdate.lonlat && storeUpdate.lonlat.longitude) || ''
                    }
                    placeholder={i18n.t('GeneralAdminInfo:longitude')}
                  />
                </div>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-sm-7 mb-sm-0 mb-3">
                <div
                  id="storeMap"
                  ref={mapRef}
                  style={{
                    ...(isSetPosByStoreClickMode
                      ? {
                          cursor: 'crosshair',
                          minHeight: '250px',
                          height: '100%',
                          width: '100%',
                        }
                      : { minHeight: '250px', height: '100%', width: '100%' }),
                  }}
                />
              </div>
              <div className="col-sm-5">
                <div className="d-flex align-items-start flex-column px-sm-2 flex-grow-1">
                  <IconButton
                    className={`mb-2 ${
                      isSetPosByStoreClickMode ? 'bg-dark' : ''
                    }`}
                    icon={<FaMapMarkedAlt />}
                    label={
                      isSetPosByStoreClickMode
                        ? i18n.t('GeneralAdminInfo:clickOnMap')
                        : i18n.t('GeneralAdminInfo:positionByMap')
                    }
                    onClick={() =>
                      setIsPosByStoreClickMode(!isSetPosByStoreClickMode)
                    }
                  />
                  <IconButton
                    className="mb-2"
                    icon={<FaAddressCard />}
                    label={i18n.t('GeneralAdminInfo:positionByAddress')}
                    onClick={localizeStoreFromAddress}
                  />
                  {storeUpdate.lonlat && (
                    <IconButton
                      className="mb-2"
                      icon={<FaExternalLinkAlt />}
                      label={i18n.t(
                        'GeneralAdminInfo:checkPostionOnGoogleMaps',
                      )}
                      tag="a"
                      target="_blank"
                      href={`https://www.google.com/maps/search/?api=1&query=${storeUpdate.lonlat.latitude},${storeUpdate.lonlat.longitude}`}
                    />
                  )}
                </div>
              </div>
            </div>
          </EditItem>

          {/* Contact info */}
          <EditItem title={i18n.t('GeneralAdminInfo:contactInfo')}>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Common:phonenumber')}
              </span>
              <input
                type="text"
                className="form-control"
                value={storeUpdate.phone_number || ''}
                maxLength={40}
                name="phone_number"
                placeholder={i18n.t('Common:phonenumberExample')}
                onChange={handleChange}
              />
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('Common:website')}
              </span>
              <input
                className="form-control"
                name="website"
                value={storeUpdate.website || ''}
                onChange={handleChange}
                type="text"
                placeholder={'http://...'}
              />
              {storeUpdate.website && !isValidUrl(storeUpdate.website) && (
                <div className="invalid-feedback d-block">
                  {i18n.t('GeneralAdminInfo:incorectUrlFeedback')}
                </div>
              )}
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('GeneralAdminInfo:newsletter')}
              </span>
              <input
                type="text"
                className={'form-control'}
                name="partner_newsletter_email"
                autoComplete="off"
                lang="en-US"
                onChange={(e) => {
                  handleChange(e);
                }}
                value={storeUpdate.partner_newsletter_email || ''}
                placeholder={i18n.t('GeneralAdminInfo:newsletterHelp')}
              />
              {storeUpdate.partner_newsletter_email &&
                !isValidEmailInput(storeUpdate.partner_newsletter_email) && (
                  <div className="invalid-feedback d-block">
                    {i18n.t('GeneralAdminInfo:incorrectNewsletterEmail')}
                  </div>
                )}
            </div>

            {/* Lieferando */}
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                value=""
                id="lieferando"
              />
              <label className="form-check-label" htmlFor="lieferando">
                {i18n.t('GeneralAdminInfo:lieferando')}
              </label>
            </div>

            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('GeneralAdminInfo:lieferando')}
              </span>
              <input
                type="text"
                className={'form-control'}
                name="lieferando_url"
                autoComplete="off"
                lang="en-US"
                onChange={handleChange}
                value={storeUpdate.lieferando_url || ''}
                placeholder="https://lieferando.de/..."
              />
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('GeneralAdminInfo:wolt')}
              </span>
              <input
                type="text"
                className={'form-control'}
                name="wolt_url"
                autoComplete="off"
                lang="en-US"
                onChange={handleChange}
                value={storeUpdate.wolt_url || ''}
                placeholder="https://wolt.com/..."
              />
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('GeneralAdminInfo:habgusta')}
              </span>
              <input
                type="text"
                className={'form-control'}
                name="habgusta_url"
                autoComplete="off"
                lang="en-US"
                onChange={handleChange}
                value={storeUpdate.habgusta_url || ''}
                placeholder="https://www.habgusta.at/..."
              />
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('GeneralAdminInfo:mjam')}
              </span>
              <input
                type="text"
                className={'form-control'}
                name="mjam_url"
                autoComplete="off"
                lang="en-US"
                onChange={handleChange}
                value={storeUpdate.mjam_url || ''}
                placeholder="https://www.mjam.net/..."
              />
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('GeneralAdminInfo:labonapp')}
              </span>
              <input
                type="text"
                className={'form-control'}
                name="labonapp_url"
                autoComplete="off"
                lang="en-US"
                onChange={handleChange}
                value={storeUpdate.labonapp_url || ''}
                placeholder="https://www.labonapp.fr/..."
              />
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('GeneralAdminInfo:ubereats')}
              </span>
              <input
                type="text"
                className={'form-control'}
                name="ubereats_url"
                autoComplete="off"
                lang="en-US"
                onChange={handleChange}
                value={storeUpdate.ubereats_url || ''}
                placeholder="https://www.ubereats.com..."
              />
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('GeneralAdminInfo:splitApp')}
              </span>
              <input
                type="text"
                className={'form-control'}
                name="split_app_url"
                autoComplete="off"
                lang="en-US"
                onChange={handleChange}
                value={storeUpdate.split_app_url || ''}
                placeholder="https://split-app.de/go/..."
              />
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('GeneralAdminInfo:rappi')}
              </span>
              <input
                type="text"
                className={'form-control'}
                name="rappi_url"
                autoComplete="off"
                lang="en-US"
                onChange={handleChange}
                value={storeUpdate.rappi_url || ''}
                placeholder="https://rappi.com-mx/..."
              />
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('GeneralAdminInfo:didiFood')}
              </span>
              <input
                type="text"
                className={'form-control'}
                name="didi_food_url"
                autoComplete="off"
                lang="en-US"
                onChange={handleChange}
                value={storeUpdate.didi_food_url || ''}
                placeholder="https://www.didi-food.com/es-MX/..."
              />
            </div>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('GeneralAdminInfo:foodora')}
              </span>
              <input
                type="text"
                className={'form-control'}
                name="foodora_url"
                autoComplete="off"
                lang="en-US"
                onChange={handleChange}
                value={storeUpdate.foodora_url || ''}
                placeholder="https://www.foodora.com/..."
              />
            </div>
            {!isProd && (
              <div className="input-group mb-3">
                <span className="input-group-text" id="basic-addon1">
                  {i18n.t('GeneralAdminInfo:tooGoodToGo')}
                </span>
                <input
                  type="text"
                  className={'form-control'}
                  name="too_good_to_go_url"
                  autoComplete="off"
                  lang="en-US"
                  onChange={handleChange}
                  value={storeUpdate.too_good_to_go_url || ''}
                  placeholder="https://toogoodtogo.de/..."
                />
              </div>
            )}

            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('GeneralAdminInfo:menuLink')}
              </span>
              <input
                type="text"
                className={
                  'form-control ' +
                  (!isNullOrWhitespace(storeUpdate.menu_link) &&
                  !isValidUrl(storeUpdate.menu_link)
                    ? 'is-invalid'
                    : '')
                }
                name="menu_link"
                autoComplete="off"
                lang="en-US"
                onChange={handleChange}
                value={storeUpdate.menu_link || ''}
                placeholder="http://..."
              />
            </div>
          </EditItem>
          {allowedToAccess && (
            <EditItem title={i18n.t('GeneralAdminInfo:privateContactPerson')}>
              <SalesforceContacts storeId={externalPickpackId} />
            </EditItem>
          )}
          <AcceptedContainersSection
            storeUpdate={storeUpdate}
            allowedToAccess={allowedToAccess}
            handleAcceptedContainerGroupsChange={
              handleAcceptedContainerGroupsChange
            }
          />
          <EditItem title={i18n.t('GeneralAdminInfo:categories')}>
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('GeneralAdminInfo:selectCategories')}
              </span>
              <MultiSelect
                style={{ flex: 'auto' }}
                value={storeUpdate.food_categories}
                name="food_categories"
                options={foodCategories || []}
                onChange={handleChange}
                placeholder={i18n.t('GeneralAdminInfo:categoriesPlaceholder')}
                maxSelectedLabels={5}
              />
            </div>
            {(storeUpdate.food_categories || [])
              .map((c: any) =>
                (foodCategories || []).find((c2: any) => c2.value === c),
              )
              .filter((c: any) => c != null)
              .map((c: { label: string; value: string }, index: number) => (
                <div
                  key={c.value}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginLeft: '1em',
                    marginRight: '1em',
                    marginTop: '0.75em',
                  }}
                >
                  <p>{`${index + 1}. ${c.label}`}</p>
                  <div>
                    <button
                      onClick={() => swapFoodCategories(index, index - 1)}
                      style={{
                        border: '#00000000',
                        background: '#00000000',
                        marginRight: '1em',
                      }}
                    >
                      <FaArrowUp />
                    </button>
                    <button
                      onClick={() => swapFoodCategories(index, index + 1)}
                      style={{ border: '#00000000', background: '#00000000' }}
                    >
                      <FaArrowDown />
                    </button>
                  </div>
                </div>
              ))}
          </EditItem>
          <EditItem
            title={i18n.t('GeneralAdminInfo:images')}
            helpText={i18n.t('GeneralAdminInfo:imageHelpText')}
          >
            <StoreImageCarouselView pickpackStoreId={externalPickpackId} />
          </EditItem>
          <EditItem title={i18n.t('Common:description')}>
            {Object.keys(storeUpdate)
              .filter((key) => key.startsWith('description_'))
              .sort()
              .map((key) => {
                const lang = key.split('_')[1];
                return (
                  <div className="input-group mb-3">
                    <span className="input-group-text" id="basic-addon1">
                      {i18n.t(
                        `Common:lang${lang.charAt(0).toUpperCase()}${lang.slice(
                          1,
                        )}`,
                      )}
                    </span>
                    <textarea
                      className="form-control"
                      name={key}
                      value={(storeUpdate as any)[key] || ''}
                      onChange={handleChange}
                    />
                  </div>
                );
              })}
          </EditItem>
          <EditItem
            title={i18n.t('GeneralAdminInfo:deeplink')}
            helpText={i18n.t('GeneralAdminInfo:deeplinkHelpText')}
          >
            <div className="input-group mb-3">
              <span className="input-group-text" id="basic-addon1">
                {i18n.t('GeneralAdminInfo:deeplink')}
              </span>
              <input
                type="text"
                className="form-control"
                name="deeplink"
                value={storeDeepLink || ''}
                disabled
              />
            </div>
          </EditItem>
          <EditItem
            title={i18n.t('Common:status')}
            helpText={i18n.t('GeneralAdminInfo:statusHelpText')}
          >
            <div className="table-responsive">
              <table className="table-striped bg-light mb-0 table w-auto">
                <tbody>
                  <tr>
                    <td>{i18n.t('GeneralAdminInfo:storePublished')}</td>
                    <td>
                      {storeUpdate.published ? (
                        <Badge color="success">{i18n.t('Common:yes')}</Badge>
                      ) : (
                        <Badge color="danger">{i18n.t('Common:no')}</Badge>
                      )}
                    </td>
                  </tr>
                  <tr>
                    <td>{i18n.t('GeneralAdminInfo:preorderStore')}</td>
                    <td>
                      {storeUpdate.can_preorder ? (
                        <Badge color="success">{i18n.t('Common:yes')}</Badge>
                      ) : (
                        <Badge color="danger">{i18n.t('Common:no')}</Badge>
                      )}
                    </td>
                  </tr>
                  {storeUpdate.can_deliver && (
                    <tr>
                      <td>{i18n.t('GeneralAdminInfo:deliveryStore')}</td>
                      <td>
                        {storeUpdate.can_deliver ? (
                          <Badge color="success">{i18n.t('Common:yes')}</Badge>
                        ) : (
                          <Badge color="danger">{i18n.t('Common:no')}</Badge>
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </EditItem>
        </EditSection>
      </ContentSection>
    </>
  );
};

function AcceptedContainersSection({
  storeUpdate,
  allowedToAccess,
  handleAcceptedContainerGroupsChange,
}: {
  storeUpdate: StoreAdminFragmentFragment;
  allowedToAccess: boolean;
  handleAcceptedContainerGroupsChange: (e: any) => void;
}) {
  const [showSelectedOnly, setShowSelectedOnly] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const deferredSearchQuery = useDeferredValue(searchQuery);

  const visibleGroups = useMemo(
    () =>
      (storeUpdate.container_groups || [])
        .filter((cg: ContainerGroup) => {
          const isSelected = (
            storeUpdate.accepted_container_type_group_ids || []
          ).includes(cg.id_int);
          if (showSelectedOnly) {
            return isSelected;
          }
          return allowedToAccess || !cg.admin_only || isSelected;
        })
        .map((cg: ContainerGroup) => ({
          ...cg,
          label: cg.name,
          value: cg.id_int,
        })),
    [
      allowedToAccess,
      showSelectedOnly,
      storeUpdate.accepted_container_type_group_ids,
      storeUpdate.container_groups,
    ],
  );

  const options = useMemo(() => {
    const query = deferredSearchQuery.trim().toLowerCase();
    if (!query) return visibleGroups;
    return visibleGroups.filter((cg) => {
      let searchable = cg.name || '';
      searchable += cg.label || '';
      searchable = searchable.toLowerCase();
      return searchable.includes(query);
    });
  }, [visibleGroups, deferredSearchQuery]);

  const itemTemplate = useCallback(
    (group: ContainerGroup) => {
      const canShowHelpPopover =
        !group.can_edit &&
        (storeUpdate?.accepted_container_type_group_ids || []).includes(
          group.id_int,
        );
      return (
        <div
          style={{ marginLeft: 4 }}
          className="d-flex align-items-center flex-wrap gap-1"
        >
          {canShowHelpPopover && (
            <div
              style={{
                float: 'left',
                marginTop: '0.4rem',
                marginRight: '0.4rem',
              }}
            >
              <HelpPopover
                text={i18n.t(
                  'GeneralAdminInfo:containerGroupNotEditableHelpText',
                )}
                id={`${group.name}-not-editable`}
                size="sm"
                propagateClick={false}
              />
            </div>
          )}
          <div className="d-flex align-items-center gap-1">
            {allowedToAccess && group.admin_only && (
              <FaLock
                style={{
                  width: '1rem',
                  height: '1rem',
                }}
              />
            )}
            {group.name}
          </div>
          <div className="d-flex flex-wrap">
            {group.image_urls != null &&
              group.image_urls
                .filter((url) => url != null)
                .map((url) => (
                  <ContainerTypeDisplay
                    containerType={{
                      id: 0,
                      imageUrl: url,
                      name: group.name || '',
                      hexCode: '',
                    }}
                    showName={false}
                    showContainerId={false}
                  />
                ))}
          </div>
        </div>
      );
    },
    [allowedToAccess, storeUpdate?.accepted_container_type_group_ids],
  );

  return (
    <EditItem
      title={i18n.t('GeneralAdminInfo:acceptedContainers')}
      helpText={i18n.t('GeneralAdminInfo:acceptedContainersHelpText')}
    >
      <div className="input-group d-flex flex-column mb-3 gap-3">
        <div className="d-flex align-items-center w-100 gap-3">
          <span className="input-group-text gap-2" id="basic-addon1">
            <VytalCheckboxLabelContainer
              style={{ alignItems: 'center', userSelect: 'none' }}
            >
              <Checkbox
                checked={showSelectedOnly}
                onClick={() => {
                  setShowSelectedOnly(!showSelectedOnly);
                }}
              />
              <CheckboxLabel
                onClick={() => {
                  setShowSelectedOnly(!showSelectedOnly);
                }}
              >
                {i18n.t('GeneralAdminInfo:showSelectedOnly')}
              </CheckboxLabel>
            </VytalCheckboxLabelContainer>
          </span>
          <InputGroup style={{ alignSelf: 'start', maxWidth: 250 }}>
            <InputGroupText>
              <FaSearch />
            </InputGroupText>
            <Input
              type="text"
              placeholder={i18n.t('Common:search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <InputGroupText
                style={{ cursor: 'pointer' }}
                onClick={() => setSearchQuery('')}
              >
                <FaTimes />
              </InputGroupText>
            )}
          </InputGroup>
        </div>
        <MultiSelect
          style={{ flex: 'auto' }}
          value={storeUpdate.accepted_container_type_group_ids}
          name="accepted_container_type_group_ids"
          options={options}
          itemTemplate={itemTemplate}
          itemClassName={
            showSelectedOnly
              ? 'bg-white text-dark align-items-baseline'
              : 'align-items-baseline'
          }
          onChange={handleAcceptedContainerGroupsChange}
          inline
          placeholder={i18n.t('GeneralAdminInfo:acceptedContainersPlaceholder')}
        />
      </div>
    </EditItem>
  );
}
